import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Typography, makeStyles } from '@material-ui/core';
import BuildIcon from '@material-ui/icons/BuildOutlined';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import {
  Content,
  Header,
  LinkButton,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  type CatalogApi,
} from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import type {
  TemplateListPageProps,
  TemplateWizardPageProps,
} from '@backstage/plugin-scaffolder/alpha';
import { ScaffolderPageContextMenu } from '@backstage/plugin-scaffolder-react/alpha';
import { TemplateCard } from './TemplateCard';
import { TemplatePanel } from './TemplatePanel';
import { YourRuns } from './YourRuns';
import { titleOf } from './templates';

/**
 * The Create page (DX-650; promised in specs/create-from-a-template.md): the
 * templates the catalog holds as cards, laid out as Access lays out its
 * requests, the colleague's own runs, and a template's form in a panel beside
 * them.
 *
 * It stands in for both of the template engine's pages: the list at /create
 * and a template's form at /create/templates/<namespace>/<name>. The second is
 * this same page with the panel open, so a template's address — from a link, a
 * bookmark, the back button — still opens its form. Everything past the form
 * (the run's own page, the task list, the template editor) is the engine's.
 */

/** Where a service is made while publishing is disabled: a GitHub template
 * repository and its bootstrap script (DX-203). */
const SERVICE_TEMPLATE_URL = 'https://github.com/revenexx/service-template';

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.75fr) minmax(0, 1fr)',
    gap: theme.spacing(3),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  main: { display: 'flex', flexDirection: 'column', gap: theme.spacing(4) },
  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(1.5),
  },
  heading: { fontSize: '1rem', fontWeight: 600, margin: 0 },
  aside: { fontSize: '0.8125rem', color: 'var(--ui-text-muted)' },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  /* The notice above everything: what a run here cannot do yet. Amber so it
     reads as a warning, not as one more card. */
  notice: {
    gridColumn: '1 / -1',
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: '1px solid var(--ui-warn-border)',
    background: 'var(--ui-warn-bg)',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: 'var(--ui-text)',
    '& strong': { fontWeight: 600, color: 'var(--ui-warn-text)' },
    '& a': { color: 'var(--ui-warn-text)', textDecoration: 'underline' },
  },
  empty: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px dashed var(--color-brand-200)',
    background: 'var(--ui-bg-elevated)',
    fontSize: '0.875rem',
    color: 'var(--ui-text-muted)',
    lineHeight: 1.5,
  },
  how: {
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    padding: theme.spacing(2, 2.25),
  },
  howHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  rules: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    color: 'var(--ui-text-muted)',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    '& li + li': { marginTop: theme.spacing(0.5) },
    '& strong': { color: 'var(--ui-text)' },
  },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

/** Where the templates live: a new one is a pull request there. */
const TEMPLATES_URL =
  'https://github.com/revenexx/backstage/tree/main/catalog/templates';

type Templates = { loading: boolean; error?: Error; items: Entity[] };

/**
 * The last answer per catalog client, so the list does not flash empty when
 * the page remounts between /create and a template's address.
 */
const lastAnswer = new WeakMap<CatalogApi, Entity[]>();

function useTemplates(): Templates {
  const catalogApi = useApi(catalogApiRef);
  const [templates, setTemplates] = useState<Templates>(() => {
    const known = lastAnswer.get(catalogApi);
    return { loading: !known, items: known ?? [] };
  });

  useEffect(() => {
    let live = true;
    catalogApi.getEntities({ filter: { kind: 'Template' } }).then(
      ({ items }) => {
        const sorted = [...items].sort((a, b) =>
          titleOf(a).localeCompare(titleOf(b), 'en'),
        );
        lastAnswer.set(catalogApi, sorted);
        if (live) setTemplates({ loading: false, items: sorted });
      },
      error => {
        if (live) setTemplates({ loading: false, error, items: [] });
      },
    );
    return () => {
      live = false;
    };
  }, [catalogApi]);

  return templates;
}

const HowCreatingWorks = () => {
  const classes = useStyles();
  const registerLink = useRouteRef(
    scaffolderPlugin.externalRoutes.registerComponent,
  );
  return (
    <section aria-labelledby="create-how-heading" className={classes.how}>
      <div className={classes.howHead}>
        <BuildIcon fontSize="small" aria-hidden />
        <h2 id="create-how-heading" className={classes.heading}>
          How creating works here
        </h2>
      </div>
      <ul className={classes.rules}>
        <li>
          Every repository has an <strong>owning team</strong>. It is the owner
          in the catalog and in CODEOWNERS, and it can push.
        </li>
        <li>
          Repositories are created private in revenexx, with main as the default
          branch.
        </li>
        <li>
          The new repository is in the catalog at once — no catalog-info.yaml to
          write by hand.
        </li>
        <li>
          Templates are files in this portal's repository. A new one is a pull
          request.
        </li>
      </ul>
      <div className={classes.links}>
        {registerLink && (
          <LinkButton variant="outlined" size="small" to={registerLink()}>
            Register existing repository
          </LinkButton>
        )}
        <Button
          variant="outlined"
          size="small"
          href={TEMPLATES_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Template files
        </Button>
      </div>
    </section>
  );
};

type CreateProps = {
  /** Given on a template's address: what its form is drawn with. */
  wizard?: TemplateWizardPageProps;
};

const Create = ({ wizard }: CreateProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const params = useParams();
  const rootLink = useRouteRef(scaffolderPlugin.routes.root);
  const templateLink = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const editLink = useRouteRef(scaffolderPlugin.routes.edit);
  const actionsLink = useRouteRef(scaffolderPlugin.routes.actions);
  const tasksLink = useRouteRef(scaffolderPlugin.routes.listTasks);
  const templates = useTemplates();

  // Bumped when a run started in the panel finishes, so Your runs asks again.
  const [runsSeen, setRunsSeen] = useState(0);
  const runFinished = useCallback(() => setRunsSeen(seen => seen + 1), []);

  const chosenRef =
    wizard && params.templateName
      ? stringifyEntityRef({
          kind: 'Template',
          namespace: params.namespace ?? 'default',
          name: params.templateName,
        })
      : undefined;
  const chosen = templates.items.find(
    template => stringifyEntityRef(template) === chosenRef,
  );

  const choose = (template: Entity) =>
    navigate(
      templateLink({
        namespace: template.metadata.namespace ?? 'default',
        templateName: template.metadata.name,
      }),
    );

  let list;
  if (templates.loading) {
    list = <Progress />;
  } else if (templates.error) {
    list = <ResponseErrorPanel error={templates.error} />;
  } else if (templates.items.length === 0) {
    list = (
      <Typography className={classes.empty}>
        No templates found. The catalog holds no template yet — one is added as
        a file in this portal's repository.
      </Typography>
    );
  } else {
    list = (
      <div className={classes.cards}>
        {templates.items.map(template => (
          <TemplateCard
            key={stringifyEntityRef(template)}
            template={template}
            onChoose={() => choose(template)}
          />
        ))}
      </div>
    );
  }

  return (
    <Page themeId="home">
      <Header
        title="Create"
        subtitle="Start a new repository from a template. It belongs to a team from the first commit and is in the catalog straight away."
      >
        <ScaffolderPageContextMenu
          onEditorClicked={() => navigate(editLink())}
          onActionsClicked={() => navigate(actionsLink())}
          onTasksClicked={() => navigate(tasksLink())}
        />
      </Header>
      <Content>
        <div className={classes.layout}>
          {/* Said on the page rather than hidden with it (DX-546): the page
              stays reachable from the catalog, the palette and its address,
              and a reader who arrives should know before they fill in a form
              that its run stops at the publish step (DX-238). The way that
              does work meanwhile is named here too (DX-203): a service from
              the template repository, made with the reader's own rights. */}
          <div role="status" className={classes.notice}>
            <strong>Publishing to GitHub is disabled.</strong> The portal's
            GitHub App can only read, so a run stops at the step that creates
            the repository. Until it can write, Create is not offered on the
            home page or in the navigation. A new service is made meanwhile from
            the{' '}
            <a
              href={SERVICE_TEMPLATE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              service template on GitHub
            </a>
            , with your own rights; its README says how.
          </div>
          <div className={classes.main}>
            <section aria-labelledby="create-templates-heading">
              <div className={classes.sectionHead}>
                <h2 id="create-templates-heading" className={classes.heading}>
                  Templates
                </h2>
                <span className={classes.aside}>
                  Repositories are created private in revenexx on GitHub
                </span>
              </div>
              {list}
            </section>
            <YourRuns refreshKey={runsSeen} />
          </div>
          <HowCreatingWorks />
        </div>

        {wizard && chosenRef && (
          <TemplatePanel
            key={chosenRef}
            templateRef={chosenRef}
            template={chosen}
            extensions={wizard.customFieldExtensions}
            layouts={wizard.layouts}
            formProps={wizard.formProps}
            onClose={() => navigate(rootLink())}
            onRunFinished={runFinished}
          />
        )}
      </Content>
    </Page>
  );
};

/** /create: the templates, with no form open. */
export const CreatePage = (_props: TemplateListPageProps) => <Create />;

/** /create/templates/<namespace>/<name>: the same page, the form open. */
export const CreateTemplatePage = (props: TemplateWizardPageProps) => (
  <Create wizard={props} />
);
